import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import parse from 'html-react-parser';
import { useQueryClient } from 'react-query';
import dayjs from 'dayjs';

import { translate as t } from 'hooks/useTranslations';
import useIssuedManualAcknowledgementSchedules from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManualAcknowledgementSchedules';
import useManageAcknowledgementSchedules from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useManageAcknowledgementSchedules';
import { ManualAcknowledgementScheduleRequest } from 'views/App/Portal/Policy/_types/acknowledgements';
import errorHelper from 'helpers/errorHelper';
import { ValidationErrors } from '../_types/errors';
import AcknowledgementScheduleForm from '../_components/AcknowledgementScheduleForm';
import { ManualGroup } from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManualGroups';

const EditAcknowledgementSchedule: React.FC = () => {
  const queryClient = useQueryClient();
  const { id, scheduleId } = useParams();
  const navigate = useNavigate();
  const schedulesQuery = useIssuedManualAcknowledgementSchedules(id ?? '');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [externalErrors, setExternalErrors] = useState<ValidationErrors>({});
  const { update: updateManualAcknowledgementSchedule } = useManageAcknowledgementSchedules();

  const manualSchedule = useMemo(() => {
    let schedules =
      schedulesQuery.data && schedulesQuery.data.pages ? schedulesQuery.data.pages.flatMap((x) => x.content) : [];
    const schedule = schedules.find((x) => x.id === scheduleId);
    return schedule;
  }, [scheduleId, schedulesQuery.data]);

  const handleSubmit = (payload: Omit<ManualAcknowledgementScheduleRequest, 'id' | 'manualId' | 'status'>) => {
    if (!id || !scheduleId) return;
    if (!manualSchedule) return;
    setIsSaving(true);
    const data: ManualAcknowledgementScheduleRequest = {
      ...payload,
      id: scheduleId,
      manualId: id,
      status: manualSchedule.status ?? 'ENABLED',
    };
    updateManualAcknowledgementSchedule(
      id,
      scheduleId,
      data,
      (_jobId) => {
        setIsSaving(false);
        enqueueSnackbar(
          parse(
            t(`policy.acknowledgementSchedules.edit.updateConfirmation`, '', {
              title: payload.title ?? '',
            })
          ),
          {
            variant: 'success',
            autoHideDuration: 6000,
          }
        );
        queryClient.invalidateQueries(['issued_manual_acknowledgment_schedules']);
        navigate(`../${id}/acknowledgement-schedules`);
      },
      (errors) => {
        setIsSaving(false);
        errorHelper.enqueueErrors(errors);
        if (errors.errorDetails) {
          const backendErrors: ValidationErrors = {};
          errors.errorDetails.forEach((error) => {
            if (error.detail.includes('title')) {
              backendErrors.title = error.detail;
            } else if (error.detail.includes('startAt')) {
              backendErrors.startAt = error.detail;
            } else if (error.detail.includes('dueDays')) {
              backendErrors.dueDays = error.detail;
            } else if (error.detail.includes('groups')) {
              backendErrors.groups = error.detail;
            } else if (error.detail.includes('policies')) {
              backendErrors.policies = error.detail;
            }
          });
          setExternalErrors(backendErrors);
        }
      }
    );
  };

  if (!id || !scheduleId) return null;
  if (!manualSchedule) return null;

  const initialValues = manualSchedule
    ? {
      title: manualSchedule.title,
      startAt: dayjs(manualSchedule.startAt),
      frequency: String(manualSchedule.frequency ?? ''),
      dueDays: manualSchedule.dueDays ?? 30,
      groups: (manualSchedule.groups ?? []) as ManualGroup[],
      policies: (manualSchedule.policies ?? []).map((x: any) => x.id),
    }
    : undefined;

  return (
    <AcknowledgementScheduleForm
      title={t('policy.acknowledgementSchedules.edit.title')}
      manualId={id}
      mode='edit'
      onCancelHref={`../${id}/acknowledgement-schedules`}
      onSubmit={handleSubmit as any}
      isSaving={isSaving}
      externalErrors={externalErrors}
      initialValues={initialValues}
    />
  );
};

export default EditAcknowledgementSchedule;
